import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

// Chart.js bileşenlerini kaydet
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default function CardAnalyticsPage() {
  const { currentUser, userRole } = useAuth();
  const { t } = useTranslation();

  // State tanımları
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [questionForms, setQuestionForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [responses, setResponses] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [availableTags, setAvailableTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  // Workspace'leri çekme
  useEffect(() => {
    const wsRef = collection(db, 'workspaces');
    const unsubscribe = onSnapshot(wsRef, (snapshot) => {
      const wsData = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setWorkspaces(userRole === 'admin' ? wsData : filterWorkspacesForUser(wsData));
    });
    return () => unsubscribe();
  }, [userRole, currentUser]);

  // Kanalları ve tag'leri çekme
  useEffect(() => {
    if (!selectedWorkspace) return;
    const fetchChannels = async () => {
      const q = query(collection(db, 'channels'), where('workspaceId', '==', selectedWorkspace.id));
      const chSnap = await getDocs(q);
      const channelsData = chSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setChannels(channelsData);

      const tags = new Set();
      channelsData.forEach((channel) => {
        channel.memberDetails?.forEach((member) => {
          member.tags?.forEach((tag) => tags.add(tag));
        });
      });
      setAvailableTags(Array.from(tags));
    };
    fetchChannels();
  }, [selectedWorkspace]);

  // Formları çekme
  useEffect(() => {
    if (!selectedChannel) return;
    const fetchForms = async () => {
      const q = query(collection(db, 'questionForms'), where('channelId', '==', selectedChannel.id));
      const formsSnap = await getDocs(q);
      setQuestionForms(formsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    };
    fetchForms();
  }, [selectedChannel]);

  // Soruları ve yanıtları çekme
  useEffect(() => {
    if (!selectedForm) return;
    const fetchData = async () => {
      setLoading(true);
      const qQuestions = query(collection(db, 'questions'), where('questionFormId', '==', selectedForm.id));
      const questionsSnap = await getDocs(qQuestions);
      const questionsData = questionsSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setQuestions(questionsData);

      const qResponses = query(collection(db, 'responses'), where('questionFormId', '==', selectedForm.id));
      const respSnap = await getDocs(qResponses);
      setResponses(respSnap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    };
    fetchData();
  }, [selectedForm]);

  // Filtrelenmiş yanıtlar
  const filteredResponses = useMemo(() => {
    if (!selectedTags.length) return responses;
    return responses.filter((resp) => {
      const userTags = selectedChannel?.memberDetails?.find((m) => m.userId === resp.userId)?.tags || [];
      return selectedTags.some((tag) => userTags.includes(tag));
    });
  }, [responses, selectedTags, selectedChannel]);

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  };

  // Grafik ve tablo verilerini hazırla
  const chartData = useMemo(() => {
    if (!questions.length || !filteredResponses.length) return {};

    const dataByQuestion = {};
    questions.forEach((q) => {
      const { questionType, text, options, ratingMin = 0, ratingMax = 5 } = q;

      if (!['singleChoice', 'multipleChoice', 'ratingQuestion', 'cardSorting'].includes(questionType)) return;

      const answers = filteredResponses
        .flatMap((r) => r.finalAnswers || [])
        .filter((a) => a.questionText === text)
        .map((a) => a.answer);

      if (questionType === 'singleChoice' || questionType === 'multipleChoice') {
        const freq = {};
        answers.forEach((ans) => {
          const values = Array.isArray(ans) ? ans : [ans];
          values.forEach((val) => {
            freq[val] = (freq[val] || 0) + 1;
          });
        });
        const totalResponses = answers.length;
        const percentages = Object.fromEntries(
          Object.entries(freq).map(([key, val]) => [key, (val / totalResponses) * 100])
        );

        dataByQuestion[stripHtml(text)] = {
          chart: {
            type: 'bar',
            data: {
              labels: options || Object.keys(freq),
              datasets: [
                {
                  label: t('analytics.percentage'),
                  data: Object.values(percentages),
                  backgroundColor: 'rgba(75, 192, 192, 0.6)',
                },
              ],
            },
            options: {
              indexAxis: 'y',
              scales: {
                x: { beginAtZero: true, max: 100, title: { display: true, text: '%' } },
                y: { title: { display: true, text: t('analytics.options') } },
              },
              plugins: { title: { display: true, text: stripHtml(text) } },
            },
          },
          tableData: Object.entries(freq).map(([option, count]) => ({ option, count })),
        };
      } else if (questionType === 'ratingQuestion') {
        const freq = {};
        answers.forEach((ans) => {
          const value = parseFloat(ans);
          if (!isNaN(value)) {
            freq[value] = (freq[value] || 0) + 1;
          }
        });
        const totalResponses = answers.length;
        const labels = Array.from({ length: ratingMax - ratingMin + 1 }, (_, i) => i + ratingMin);
        const percentages = labels.map((label) => ((freq[label] || 0) / totalResponses) * 100);

        dataByQuestion[stripHtml(text)] = {
          chart: {
            type: 'bar',
            data: {
              labels,
              datasets: [
                {
                  label: t('analytics.percentage'),
                  data: percentages,
                  backgroundColor: 'rgba(102, 149, 66, 0.6)',
                },
              ],
            },
            options: {
              scales: {
                y: { beginAtZero: true, max: 100, title: { display: true, text: '%' } },
                x: { title: { display: true, text: t('analytics.ratingScale') } },
              },
              plugins: { title: { display: true, text: stripHtml(text) } },
            },
          },
          tableData: labels.map((label) => ({ label, count: freq[label] || 0 })),
        };
      } else if (questionType === 'cardSorting') {
        const groupFreq = {};
        answers.forEach((ans) => {
          if (!ans || typeof ans !== 'object' || Array.isArray(ans)) return;
          Object.entries(ans).forEach(([group, cards]) => {
            //if (group === 'unsorted') return;
            if (!groupFreq[group]) groupFreq[group] = {};
            cards.forEach((card, index) => {
              const rank = index + 1; // Determine rank from position
              const cardText = card.text;
              if (!groupFreq[group][cardText]) {
                groupFreq[group][cardText] = { count: 0, sumRank: 0 };
              }
              groupFreq[group][cardText].count += 1;
              groupFreq[group][cardText].sumRank += rank;
            });
          });
        });
      
        // Calculate average rank per card per group
        const cardAverages = [];
        Object.entries(groupFreq).forEach(([group, cards]) => {
          Object.entries(cards).forEach(([card, { sumRank, count }]) => {
            const average = sumRank / count;
            cardAverages.push({
              group,
              card,
              averageRank: average,
              count,
            });
          });
        });
      
        // Sort by group and card for consistent ordering
        cardAverages.sort((a, b) => a.group.localeCompare(b.group) || a.card.localeCompare(b.card));
      
        dataByQuestion[stripHtml(text)] = {
          chart: {
            type: 'bar',
            data: {
              labels: cardAverages.map(item => `${item.group} - ${item.card}`),
              datasets: [{
                label: t('analytics.averageRank'),
                data: cardAverages.map(item => item.averageRank),
                backgroundColor: 'rgba(255, 99, 132, 0.6)',
              }]
            },
            options: {
              indexAxis: 'y', // Horizontal bars for better readability
              scales: {
                x: { 
                  beginAtZero: true,
                  title: { display: true, text: t('analytics.averageRank') }
                },
                y: { 
                  title: { display: true, text: t('analytics.groupsAndCards') },
                  ticks: {
                    autoSkip: false // Ensure all labels are displayed
                  }
                },
              },
              plugins: { 
                title: { display: true, text: stripHtml(text) },
                tooltip: {
                  callbacks: {
                    label: (context) => {
                      const value = context.parsed.x;
                      return `${t('analytics.averageRank')}: ${value.toFixed(2)}`;
                    }
                  }
                }
              },
            },
          },
          tableData: cardAverages.map(({ group, card, averageRank, count }) => ({ 
            group, 
            card, 
            averageRank,
            count 
          })),
        };
      }
    });

    return dataByQuestion;
  }, [questions, filteredResponses, t]);

  // Render
  return (
    <Container sx={{ my: 4, py: 2 }}>
      <Typography variant="h4" gutterBottom align="center">
        {t('analytics.pageTitle') || 'Kart Analiz Sayfası'}
      </Typography>

      {/* Workspace Seçimi */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl fullWidth>
          <InputLabel>{t('analytics.workspaceLabel')}</InputLabel>
          <Select
            value={selectedWorkspace?.id || ''}
            onChange={(e) => setSelectedWorkspace(workspaces.find((w) => w.id === e.target.value) || null)}
          >
            <MenuItem value="">{t('analytics.select')}</MenuItem>
            {workspaces.map((ws) => (
              <MenuItem key={ws.id} value={ws.id}>
                {ws.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      {/* Kanal ve Form Seçimi */}
      {selectedWorkspace && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('analytics.channelLabel')}</InputLabel>
            <Select
              value={selectedChannel?.id || ''}
              onChange={(e) => setSelectedChannel(channels.find((c) => c.id === e.target.value) || null)}
            >
              <MenuItem value="">{t('analytics.select')}</MenuItem>
              {channels.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedChannel && (
            <>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{t('analytics.formLabel')}</InputLabel>
                <Select
                  value={selectedForm?.id || ''}
                  onChange={(e) => setSelectedForm(questionForms.find((f) => f.id === e.target.value) || null)}
                >
                  <MenuItem value="">{t('analytics.select')}</MenuItem>
                  {questionForms.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {availableTags.length > 0 && (
                <Autocomplete
                  multiple
                  options={availableTags}
                  value={selectedTags}
                  onChange={(event, newValue) => setSelectedTags(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('analytics.userTags') || 'Kullanıcı Tagları'}
                      placeholder={t('analytics.selectTags') || 'Tag seçin'}
                      size="small"
                    />
                  )}
                />
              )}
            </>
          )}
        </Paper>
      )}

      {/* Grafikler ve Tablolar */}
      {selectedForm && (
        <Paper sx={{ p: 2 }}>
          {loading ? (
            <CircularProgress />
          ) : Object.entries(chartData).length > 0 ? (
            Object.entries(chartData).map(([questionText, { chart, tableData }]) => (
              <Box key={questionText} sx={{ mb: 4 }}>
                <Bar data={chart.data} options={chart.options} />
                <Typography variant="h6" sx={{ mt: 2 }}>
                  {t('analytics.tableTitle', { question: questionText })}
                </Typography>
                {tableData[0]?.option ? (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('analytics.option')}</TableCell>
                        <TableCell>{t('analytics.count')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData.map((data, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{data.option}</TableCell>
                          <TableCell>{data.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : tableData[0]?.label ? (
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('analytics.rating')}</TableCell>
                        <TableCell>{t('analytics.count')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData.map((data, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{data.label}</TableCell>
                          <TableCell>{data.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                    <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('analytics.group')}</TableCell>
                        <TableCell>{t('analytics.card')}</TableCell>
                        <TableCell>{t('analytics.averageRank')}</TableCell>
                        <TableCell>{t('analytics.count')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tableData.map((data, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{data.group}</TableCell>
                          <TableCell>{data.card}</TableCell>
                          <TableCell>{data.averageRank.toFixed(2)}</TableCell>
                          <TableCell>{data.count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            ))
          ) : (
            <Typography>{t('analytics.noData') || 'Gösterilecek veri yok.'}</Typography>
          )}
        </Paper>
      )}

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Container>
  );
}

// Yardımcı fonksiyon
function filterWorkspacesForUser(wsData) {
  return wsData;
}